export const CAMPAIGN_LIMITS = {
  PAGE_TITLE_MAX_LENGTH: 50,
  PAGE_NAME_MAX_LENGTH: 50,
  TICKET_NAME_MAX_LENGTH: 25,
  TICKET_AMOUNT_MAX: 999999,
  GOAL_AMOUNT_MAX: 99_999_999,
  DESCRIPTION_MAX_LENGTH: 50,
  INVENTORY_MAX: 999_999,
  REGISTRATION_TICKET_CAPACITY_MAX: 99_999_999,
  REGISTRATION_LIMIT_MAX: 999_999,
  EVENT_PLACE_MAX_LENGTH: 100,
  EVENT_ADDRESS_MAX_LENGTH: 200,
  RECURRING_PAYMENTS_MAX: 9999,
  GROW_RECURRING_PAYMENTS_MAX: 48,
  RECURRING_PAYMENTS_MIN: 2,
  TICKET_FIELD_NAME_MAX_LENGTH: 30,
  TICKET_FIELD_LABEL_MAX_LENGTH: 30,
  GROUP_NAME_MAX_LENGTH: 50,
  GROUP_CREATOR_NAME_MAX_LENGTH: 50,
  PUBLIC_AMOUNT_MULTIPLIER_MIN: 2,
  PUBLIC_AMOUNT_MULTIPLIER_MAX: 10
} as const;

export function recurringPaymentsMaxForProvider(providerType?: string | null): number {
  return providerType === 'grow' ? CAMPAIGN_LIMITS.GROW_RECURRING_PAYMENTS_MAX : CAMPAIGN_LIMITS.RECURRING_PAYMENTS_MAX;
}

export function donationUnitNameError(value: unknown): string | null {
  if (!value || !String(value).trim()) {
    return 'שם הוא שדה חובה';
  }
  if (String(value).length > CAMPAIGN_LIMITS.TICKET_NAME_MAX_LENGTH) {
    return `שם מוגבל ל-${CAMPAIGN_LIMITS.TICKET_NAME_MAX_LENGTH} תווים`;
  }
  return null;
}

export function donationUnitInventoryError(value: unknown, min = 0): string | null {
  if (value === '' || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n < min) {
    return min > 0 ? `מלאי חייב להיות לפחות ${min.toLocaleString('he-IL')}` : 'מלאי לא יכול להיות שלילי';
  }
  if (n > CAMPAIGN_LIMITS.INVENTORY_MAX) {
    return `מלאי מוגבל ל-${CAMPAIGN_LIMITS.INVENTORY_MAX.toLocaleString('he-IL')}`;
  }
  return null;
}

export function recurringNumberOfPaymentsError(
  isRecurring: boolean | undefined,
  value: unknown,
  max: number = CAMPAIGN_LIMITS.RECURRING_PAYMENTS_MAX
): string | null {
  if (!isRecurring) return null;
  if (value === '' || value == null) {
    return 'מספר תשלומים הוא שדה חובה בהוראת קבע';
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n < CAMPAIGN_LIMITS.RECURRING_PAYMENTS_MIN) {
    return `מספר תשלומים חייב להיות לפחות ${CAMPAIGN_LIMITS.RECURRING_PAYMENTS_MIN}`;
  }
  if (n > max) {
    return `מספר התשלומים בהוראת קבע מוגבל ל-${max}`;
  }
  return null;
}

export const DONATION_RECURRING_TOTAL_MAX = CAMPAIGN_LIMITS.TICKET_AMOUNT_MAX * CAMPAIGN_LIMITS.RECURRING_PAYMENTS_MAX;
const installmentCountForRule = (isRecurring: boolean, recurringNumberOfPayments?: number | null): number => {
  if (!isRecurring) return 1;
  const raw = Number(recurringNumberOfPayments);
  const n = Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
  return Math.min(n, CAMPAIGN_LIMITS.RECURRING_PAYMENTS_MAX);
};
export const getMaxDonationTotalForPayments = (
  isRecurring: boolean,
  recurringNumberOfPayments?: number | null
): number =>
  isRecurring
    ? CAMPAIGN_LIMITS.TICKET_AMOUNT_MAX * installmentCountForRule(true, recurringNumberOfPayments)
    : CAMPAIGN_LIMITS.TICKET_AMOUNT_MAX;
export const validateDonationTotalPerInstallmentRule = (
  total: number,
  isRecurring: boolean,
  recurringNumberOfPayments?: number | null
): string | null => {
  if (!Number.isFinite(total)) return null;
  const n = installmentCountForRule(isRecurring, recurringNumberOfPayments);
  const per = n > 0 ? total / n : total;
  if (per > CAMPAIGN_LIMITS.TICKET_AMOUNT_MAX) {
    const maxTotal = CAMPAIGN_LIMITS.TICKET_AMOUNT_MAX * n;
    if (!isRecurring || n <= 1) {
      return `סכום חד-פעמי מוגבל ל-${CAMPAIGN_LIMITS.TICKET_AMOUNT_MAX.toLocaleString('he-IL')}`;
    }
    return `כל תשלום מוגבל ל-${CAMPAIGN_LIMITS.TICKET_AMOUNT_MAX.toLocaleString(
      'he-IL'
    )}; ב-${n} תשלומים ניתן עד ${maxTotal.toLocaleString('he-IL')} בסך הכל`;
  }
  return null;
};
export const MAX_FREE_FORM_TICKET_FIELDS = 10;
export const FREE_FORM_TICKET_FIELD_NAMES: string[] = Array.from(
  { length: MAX_FREE_FORM_TICKET_FIELDS },
  (_, i) => `freeField${i + 1}`
);
export function isFreeFormTicketFieldName(name: string): boolean {
  return FREE_FORM_TICKET_FIELD_NAMES.includes(name);
}

/**
 * Open-amount commitments: the donor types the amount on the public payment page.
 * Whole shekels only — every display path formats with `maximumFractionDigits: 0`,
 * so agorot would be shown rounded while a different amount is charged.
 */
export const OPEN_COMMITMENT_AMOUNT_MIN = 1;
export const OPEN_COMMITMENT_AMOUNT_MAX = 1_000_000;

/** The donor-facing message for an invalid open amount, or null when it is valid. */
export function openCommitmentAmountError(value: unknown): string | null {
  if (value === '' || value == null) return 'יש להזין סכום';
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return 'יש להזין סכום בשקלים שלמים';
  if (n < OPEN_COMMITMENT_AMOUNT_MIN) {
    return `הסכום המינימלי הוא ${OPEN_COMMITMENT_AMOUNT_MIN.toLocaleString('he-IL')} ש"ח`;
  }
  if (n > OPEN_COMMITMENT_AMOUNT_MAX) {
    return `הסכום המקסימלי הוא ${OPEN_COMMITMENT_AMOUNT_MAX.toLocaleString('he-IL')} ש"ח`;
  }
  return null;
}
